import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * RestoreLlynGwynantCard (iter151)
 *
 * One-tap recovery for the Llyn Gwynant trip that was cascade-deleted by
 * the Dulas legacy-campsite purge in July 2026. Calls Gemini Nano Banana
 * server-side to regenerate a "Porto 6 red wine on the picnic table"
 * photo from the operator's memory. Guarded so repeat clicks don't
 * duplicate the story.
 */
export default function RestoreLlynGwynantCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/restore/llyn-gwynant/status");
      setStatus(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/restore/llyn-gwynant");
      if (data.already_restored) {
        toast.info("Already restored — story is already on the wall.");
      } else if (data.ok) {
        toast.success("Llyn Gwynant trip restored — reload the community wall.");
      }
      loadStatus();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const restoredAt = status?.ran_at;
  const restoredPretty = restoredAt
    ? new Date(restoredAt).toLocaleString("en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-restore-llyn-gwynant"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <RotateCcw className="w-3 h-3" /> Trip restoration
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            Restore Llyn Gwynant trip
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Recovers the trip post cascade-deleted by the July 2026 Dulas
            legacy cleanup. Uses AI image generation for the Porto 6 wine
            photo. Runs once — button locks after success.
          </p>
        </div>
        {restoredPretty && (
          <div
            className="text-[11px] px-2 py-1 rounded-full bg-[#ecfdf5] text-[#065f46] font-semibold flex items-center gap-1 shrink-0"
            data-testid="restore-llyn-gwynant-status"
          >
            <CheckCircle2 className="w-3 h-3" />
            Restored {restoredPretty}
          </div>
        )}
      </div>

      <div className="mt-4">
        <button
          type="button"
          disabled={busy || !!restoredPretty}
          onClick={restore}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="restore-llyn-gwynant-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          {restoredPretty ? "Already restored" : "Restore trip now"}
        </button>
        {busy && (
          <span className="ml-3 text-xs text-[#666]">
            Generating the wine photo — about 15 seconds…
          </span>
        )}
      </div>
    </section>
  );
}
